import { z } from 'zod';
import {
	TOOL_NAME_ADD_RELATION,
	TOOL_NAME_CREATE_PERSPECTIVE,
	TOOL_NAME_GET_MODEL_SCHEMA,
	TOOL_NAME_GET_PERSPECTIVE,
	TOOL_NAME_LIST_DATA_MODELS,
	TOOL_NAME_LIST_PERSPECTIVES,
	TOOL_NAME_REMOVE_RELATION,
	TOOL_NAME_TRIGGER_BUILD,
	TOOL_NAME_UPDATE_PERSPECTIVE,
	toEngineName,
} from '@/constants';
import { devDebug } from '@/utils/dev-debug';
import {
	triggerFullBuild,
	pollBuild,
} from '@/engines/build-csv-model/rest-build';
import { getHttpClient } from '@/singleton-clients';
import { nanoidUUIDv4 } from '@/utils/id-utils';
import {
	listDataModelsParamsSchema,
	getModelSchemaParamsSchema,
	addRelationParamsSchema,
	removeRelationParamsSchema,
	createPerspectiveParamsSchema,
	updatePerspectiveParamsSchema,
	getPerspectiveParamsSchema,
	triggerBuildParamsSchema,
	listPerspectivesParamsSchema,
	ListDataModelsResult,
	ModelSchemaResult,
	AddRelationResult,
	RemoveRelationResult,
	CreatePerspectiveResult,
	UpdatePerspectiveResult,
	GetPerspectiveResult,
	TriggerBuildResult,
	ListPerspectivesResult,
	PerspectiveEngineContext,
} from './types';

const ECM_GRAPHQL_URL = 'api/v2/ecm/';
const PERSPECTIVES_URL = 'api/v2/perspectives';

const ELASTICUBES_METADATA_QUERY = `query elasticubesMetadata {
  elasticubesMetadata {
    oid
    title
    type
    status
    lastBuildTime
  }
}`;

const ELASTICUBE_BY_OID_QUERY = `query elasticubeByOid($cubeOid: UUID!) {
  elasticube: elasticubeByOid(oid: $cubeOid) {
    oid
    title
    datasets {
      oid
      connection { oid }
      schema {
        tables {
          oid
          name
          columns { oid name type }
        }
      }
    }
    relations {
      oid
      columns { dataset table column isDropped }
    }
  }
}`;

const ADD_RELATION_MUTATION = `mutation addRelation($elasticubeOid: UUID!, $source: RelationReferenceInput!, $target: RelationReferenceInput!, $suggestedOid: UUID!) {
  addRelation(elasticubeOid: $elasticubeOid, source: $source, target: $target, suggestedOid: $suggestedOid) {
    oid
  }
}`;

const REMOVE_RELATION_EDGES_MUTATION = `mutation removeRelationEdges($elasticubeOid: UUID!, $relationOid: UUID!, $edges: [RelationReferenceInput!]!) {
  relations: removeRelationEdges(elasticubeOid: $elasticubeOid, relationOid: $relationOid, edges: $edges) {
    oid
    columns { column }
  }
}`;

interface GraphQLElasticube {
	oid: string;
	title: string;
	datasets: Array<{
		oid: string;
		connection?: { oid: string } | null;
		schema: {
			tables: Array<{
				oid: string;
				name: string;
				columns: Array<{ oid: string; name: string; type: number }>;
			}>;
		};
	}>;
	relations: Array<{
		oid: string;
		columns: Array<{
			dataset: string;
			table: string;
			column: string;
			isDropped?: boolean;
		}>;
	}>;
}

interface GraphQLAddRelationResponse {
	data?: { addRelation?: { oid: string } | null };
	errors?: Array<{ message: string; extensions?: Record<string, unknown> }>;
}

interface GraphQLResponse {
	data: { elasticube: GraphQLElasticube };
}

interface GraphQLMetadataResponse {
	data: {
		elasticubesMetadata: Array<{
			oid: string;
			title: string;
			type: string;
			status: string[] | string | null;
			lastBuildTime: string | null;
		}>;
	};
}

export async function listDataModelsEngine(
	_params: z.infer<typeof listDataModelsParamsSchema>,
	context: PerspectiveEngineContext,
): Promise<ListDataModelsResult> {
	devDebug('engineCall', {
		engine: toEngineName(TOOL_NAME_LIST_DATA_MODELS),
		toolCallId: context.toolCallId,
	});

	const httpClient = context.httpClient ?? getHttpClient();

	const response = await httpClient.post<GraphQLMetadataResponse>(
		ECM_GRAPHQL_URL,
		{
			operationName: 'elasticubesMetadata',
			query: ELASTICUBES_METADATA_QUERY,
		},
		{
			headers: {
				Accept: 'application/json, text/plain, */*',
				'Content-Type': 'application/json;charset=UTF-8',
				internal: 'true',
			},
		},
	);

	const models = (response?.data?.elasticubesMetadata ?? []).map((m) => ({
		oid: m.oid,
		title: m.title,
		type: m.type,
		lastBuildTime: m.lastBuildTime,
	}));

	return { models };
}

export async function getModelSchemaEngine(
	params: z.infer<typeof getModelSchemaParamsSchema>,
	context: PerspectiveEngineContext,
): Promise<ModelSchemaResult> {
	const { datamodelOid } = params;
	devDebug('engineCall', {
		engine: toEngineName(TOOL_NAME_GET_MODEL_SCHEMA),
		datamodelOid,
		toolCallId: context.toolCallId,
	});

	const httpClient = context.httpClient ?? getHttpClient();

	const response = await httpClient.post<GraphQLResponse>(
		ECM_GRAPHQL_URL,
		{
			operationName: 'elasticubeByOid',
			variables: { cubeOid: datamodelOid },
			query: ELASTICUBE_BY_OID_QUERY,
		},
		{
			headers: {
				Accept: 'application/json, text/plain, */*',
				'Content-Type': 'application/json;charset=UTF-8',
				internal: 'true',
			},
		},
	);

	const ec = response?.data?.elasticube;
	if (!ec) {
		throw new Error(`Data model not found for OID: ${datamodelOid}`);
	}

	const tables = ec.datasets.flatMap((ds) =>
		(ds.schema?.tables ?? []).map((t) => ({ ...t, datasetOid: ds.oid })),
	);

	const relations = ec.relations.map((r) => ({
		oid: r.oid,
		tables: [...new Set(r.columns.map((c) => c.table))],
	}));

	return { datamodelOid: ec.oid, title: ec.title, tables, relations };
}

export async function addRelationEngine(
	params: z.infer<typeof addRelationParamsSchema>,
	context: PerspectiveEngineContext,
): Promise<AddRelationResult> {
	const { datamodelOid, source, target } = params;
	devDebug('engineCall', {
		engine: toEngineName(TOOL_NAME_ADD_RELATION),
		datamodelOid,
		toolCallId: context.toolCallId,
	});

	// LLM commonly mixes up the three UUID flavors (datamodelOid / datasetOid /
	// tableOid). Resolve a fresh schema, then validate + auto-correct the
	// `dataset` field by looking up each table's actual parent datasetOid.
	const schema = await getModelSchemaEngine({ datamodelOid }, context);
	const tableByOid = new Map(schema.tables.map((t) => [t.oid, t]));

	const reconcile = (
		ref: { dataset: string; table: string; column: string },
		label: 'source' | 'target',
	) => {
		const tbl = tableByOid.get(ref.table);
		if (!tbl) {
			throw new Error(
				`addRelation: ${label}.table OID ${ref.table} is not a table in ` +
					`datamodel ${datamodelOid}. Re-run getModelSchema.`,
			);
		}
		if (!tbl.columns.find((c) => c.oid === ref.column)) {
			throw new Error(
				`addRelation: ${label}.column OID ${ref.column} is not a ` +
					`column of table "${tbl.name}". Re-run getModelSchema.`,
			);
		}
		if (ref.dataset !== tbl.datasetOid) {
			console.warn(
				`${TOOL_NAME_ADD_RELATION}: auto-correcting ${label}.dataset ` +
					`(${ref.dataset} → ${tbl.datasetOid}) for table "${tbl.name}".`,
			);
			return { ...ref, dataset: tbl.datasetOid };
		}
		return ref;
	};

	const fixedSource = reconcile(source, 'source');
	const fixedTarget = reconcile(target, 'target');

	const httpClient = context.httpClient ?? getHttpClient();

	const response = await httpClient.post<GraphQLAddRelationResponse>(
		ECM_GRAPHQL_URL,
		{
			operationName: 'addRelation',
			variables: {
				elasticubeOid: datamodelOid,
				source: fixedSource,
				target: fixedTarget,
				suggestedOid: nanoidUUIDv4(),
			},
			query: ADD_RELATION_MUTATION,
		},
		{
			headers: {
				Accept: 'application/json, text/plain, */*',
				'Content-Type': 'application/json;charset=UTF-8',
				internal: 'true',
			},
		},
	);

	const relationOid = response?.data?.addRelation?.oid;
	if (!relationOid) {
		// Surface the actual GraphQL error / response so the assistant has something
		// to act on instead of "OIDs are wrong" (often misleading — see SNS-* logs
		// where suggestedOid was missing and we silently said the OIDs were bad).
		const gqlErrors = response?.errors
			?.map((e) => e.message)
			.filter(Boolean)
			.join('; ');
		const detail =
			gqlErrors || `response: ${JSON.stringify(response).slice(0, 500)}`;
		console.error(TOOL_NAME_ADD_RELATION, 'failed', {
			datamodelOid,
			source,
			target,
			response,
		});
		throw new Error(`addRelation failed — ${detail}`);
	}

	return { relationOid, datamodelOid };
}

interface GraphQLRemoveRelationEdgesResponse {
	data?: {
		relations?: { oid: string; columns: Array<{ column: string }> } | null;
	};
	errors?: Array<{ message: string; extensions?: Record<string, unknown> }>;
}

/**
 * Pull whatever the underlying HttpClient stashed on the thrown error — Sisense's
 * HttpClient surfaces non-2xx responses as Errors with translation-key messages
 * like "errors.responseError" while keeping the real server payload on
 * `.response`, `.responseText`, `.data`, etc. We reach into a few common shapes
 * so the caller sees the actual server message instead of an i18n key.
 */
function extractHttpErrorDetail(err: unknown): {
	detail: string | undefined;
	dump: Record<string, unknown>;
} {
	if (!err || typeof err !== 'object')
		return { detail: undefined, dump: { raw: String(err) } };
	const e = err as Record<string, any>;
	const status =
		e.status ?? e.statusCode ?? e.response?.status ?? e.cause?.status;
	const body =
		e.response?.data ??
		e.response?.body ??
		e.response?._data ??
		e.responseText ??
		e.data ??
		e.body ??
		e.cause?.body;
	const errors = e.response?.data?.errors ?? e.errors ?? e.cause?.errors;
	const gql = Array.isArray(errors)
		? errors
				.map((x: any) => x?.message)
				.filter(Boolean)
				.join('; ')
		: undefined;
	const bodyStr =
		gql ??
		(typeof body === 'string'
			? body.slice(0, 1000)
			: body
			? JSON.stringify(body).slice(0, 1000)
			: undefined);
	const parts = [
		status ? `HTTP ${status}` : undefined,
		bodyStr ? `body: ${bodyStr}` : undefined,
	].filter(Boolean);

	// Snapshot every field on the thrown object — the Sisense HttpClient
	// stashes the real payload under varying keys depending on the failure
	// path (network vs 4xx vs 5xx vs CORS), so dump everything we can see.
	const dump: Record<string, unknown> = {};
	for (const key of Object.getOwnPropertyNames(e)) {
		try {
			const v = e[key];
			if (v && typeof v === 'object') {
				dump[key] = JSON.parse(
					JSON.stringify(v, (_k, val) =>
						typeof val === 'function' ? '[fn]' : val,
					),
				);
			} else {
				dump[key] = v;
			}
		} catch {
			dump[key] = `[unserializable: ${typeof e[key]}]`;
		}
	}
	if (e.message && !dump.message) dump.message = e.message;
	if (e.name && !dump.name) dump.name = e.name;

	return { detail: parts.length ? parts.join(' — ') : undefined, dump };
}

export async function removeRelationEngine(
	params: z.infer<typeof removeRelationParamsSchema>,
	context: PerspectiveEngineContext,
): Promise<RemoveRelationResult> {
	const { datamodelOid, relationOid } = params;
	devDebug('engineCall', {
		engine: toEngineName(TOOL_NAME_REMOVE_RELATION),
		datamodelOid,
		relationOid,
		toolCallId: context.toolCallId,
	});

	const httpClient = context.httpClient ?? getHttpClient();

	// Sisense ECM removes a relation by removing its edges (one per side).
	// Fetch the raw elasticube schema so we can find the relation's actual
	// column refs (table + column for each edge) and pass them to
	// removeRelationEdges. Mapped getModelSchemaEngine output drops column-level
	// detail, so we go raw.
	const schemaResp = await httpClient.post<GraphQLResponse>(
		ECM_GRAPHQL_URL,
		{
			operationName: 'elasticubeByOid',
			variables: { cubeOid: datamodelOid },
			query: ELASTICUBE_BY_OID_QUERY,
		},
		{
			headers: {
				Accept: 'application/json, text/plain, */*',
				'Content-Type': 'application/json;charset=UTF-8',
				internal: 'true',
			},
		},
	);
	const ec = schemaResp?.data?.elasticube;
	if (!ec) {
		throw new Error(
			`removeRelation: data model not found for OID ${datamodelOid}. Re-run getDataSources and use a fresh oid.`,
		);
	}
	const relation = ec.relations.find((r) => r.oid === relationOid);
	if (!relation) {
		throw new Error(
			`removeRelation: relation ${relationOid} not found on datamodel ${datamodelOid}. Re-run getModelSchema and pick an OID from relations[].`,
		);
	}
	// Send all edges so the entire join is removed in one call. Filter out
	// already-dropped edges to avoid the server complaining.
	const edges = relation.columns
		.filter((c) => !c.isDropped)
		.map((c) => ({ table: c.table, column: c.column }));
	if (edges.length === 0) {
		// All edges already dropped — treat as success so the LLM doesn't loop.
		return { relationOid, datamodelOid };
	}

	const requestBody = {
		operationName: 'removeRelationEdges',
		variables: { elasticubeOid: datamodelOid, relationOid, edges },
		query: REMOVE_RELATION_EDGES_MUTATION,
	};

	let response: GraphQLRemoveRelationEdgesResponse | undefined;
	try {
		response = await httpClient.post<GraphQLRemoveRelationEdgesResponse>(
			ECM_GRAPHQL_URL,
			requestBody,
			{
				headers: {
					Accept: 'application/json, text/plain, */*',
					'Content-Type': 'application/json;charset=UTF-8',
					internal: 'true',
				},
			},
		);
	} catch (err) {
		const { detail, dump } = extractHttpErrorDetail(err);
		const baseMsg = err instanceof Error ? err.message : String(err);
		console.error(TOOL_NAME_REMOVE_RELATION, 'http error', {
			datamodelOid,
			relationOid,
			err,
			dump,
		});
		const wrapped = new Error(
			`removeRelation failed — ${detail ?? baseMsg}` +
				` (mutation: removeRelation, args: { elasticubeOid: ${datamodelOid}, relationOid: ${relationOid} })`,
		);
		// Stash the full error dump so the assistant tool wrapper can surface it
		// in the renderer payload — see manage-perspectives-tools.tsx.
		(wrapped as Error & { sisenseDump?: unknown }).sisenseDump = dump;
		throw wrapped;
	}

	if (response?.errors?.length) {
		const detail = response.errors
			.map((e) => e.message)
			.filter(Boolean)
			.join('; ');
		console.error(TOOL_NAME_REMOVE_RELATION, 'graphql error', {
			datamodelOid,
			relationOid,
			response,
		});
		throw new Error(
			`removeRelation failed — ${detail}` +
				` (mutation: removeRelation, args: { elasticubeOid: ${datamodelOid}, relationOid: ${relationOid} })`,
		);
	}

	return { relationOid, datamodelOid };
}

export async function createPerspectiveEngine(
	params: z.infer<typeof createPerspectiveParamsSchema>,
	context: PerspectiveEngineContext,
): Promise<CreatePerspectiveResult> {
	const { datamodelOid, name, description, tables, relations } = params;
	devDebug('engineCall', {
		engine: toEngineName(TOOL_NAME_CREATE_PERSPECTIVE),
		name,
		toolCallId: context.toolCallId,
	});

	const httpClient = context.httpClient ?? getHttpClient();

	// `crypto.randomUUID()` is only available in secure contexts (HTTPS / localhost).
	// Sisense served over HTTP returns undefined — use the nanoid-backed fallback.
	const perspectiveOid = nanoidUUIDv4();

	// Sisense rejects the whole POST if any relation OID references a table
	// not in `tables`, or simply doesn't exist. The LLM consistently violates
	// this on the first try, then retries without relations and succeeds. To
	// turn that two-call dance into one call, validate + filter relations
	// against the live schema before sending.
	const safeRelations = await filterValidRelations({
		datamodelOid,
		relations: relations ?? [],
		perspectiveTableOids: tables.map((t) => t.oid),
		httpClient,
	});

	await httpClient.post(PERSPECTIVES_URL, {
		oid: perspectiveOid,
		name,
		description: description ?? '',
		datamodelOid,
		parentOid: datamodelOid,
		tables,
		relations: safeRelations,
	});

	const columnCount = tables.reduce(
		(sum, t) => sum + t.columnsDiff.length,
		0,
	);

	return {
		perspectiveOid,
		name,
		datamodelOid,
		tableCount: tables.length,
		columnCount,
	};
}

/**
 * Fetch the live schema and keep only relation entries whose endpoint tables
 * are all included in the perspective. Drops entries that reference unknown or
 * out-of-scope tables — those are exactly the ones that cause Sisense to
 * reject the POST with a "relationship identifiers" error.
 *
 * Returns `relations` unchanged if the input is empty or the schema fetch
 * fails (we never want this safety net to break a valid request).
 */
async function filterValidRelations({
	datamodelOid,
	relations,
	perspectiveTableOids,
	httpClient,
}: {
	datamodelOid: string;
	relations: Array<{ oid: string }>;
	perspectiveTableOids: string[];
	httpClient: ReturnType<typeof getHttpClient>;
}): Promise<Array<{ oid: string }>> {
	if (relations.length === 0) return relations;

	let schemaRelations: Array<{ oid: string; tables: Set<string> }>;
	try {
		const response = await httpClient.post<GraphQLResponse>(
			ECM_GRAPHQL_URL,
			{
				operationName: 'elasticubeByOid',
				variables: { cubeOid: datamodelOid },
				query: ELASTICUBE_BY_OID_QUERY,
			},
			{
				headers: {
					Accept: 'application/json, text/plain, */*',
					'Content-Type': 'application/json;charset=UTF-8',
					internal: 'true',
				},
			},
		);
		const ec = response?.data?.elasticube;
		if (!ec) return relations;
		schemaRelations = ec.relations.map((r) => ({
			oid: r.oid,
			tables: new Set(r.columns.map((c) => c.table)),
		}));
	} catch {
		return relations;
	}

	const allowed = new Set(perspectiveTableOids);
	const validOidByOid = new Map(schemaRelations.map((r) => [r.oid, r]));

	const safe: Array<{ oid: string }> = [];
	const dropped: string[] = [];
	for (const r of relations) {
		const meta = validOidByOid.get(r.oid);
		if (!meta) {
			dropped.push(r.oid);
			continue;
		}
		const allInScope = [...meta.tables].every((t) => allowed.has(t));
		if (!allInScope) {
			dropped.push(r.oid);
			continue;
		}
		safe.push({ oid: r.oid });
	}

	if (dropped.length > 0) {
		devDebug('toolCall', {
			tool: 'filterValidRelations',
			phase: 'createPerspective',
			droppedInvalidRelationOids: dropped,
			droppedCount: dropped.length,
		});
	}
	return safe;
}

export async function updatePerspectiveEngine(
	params: z.infer<typeof updatePerspectiveParamsSchema>,
	context: PerspectiveEngineContext,
): Promise<UpdatePerspectiveResult> {
	const { perspectiveOid, ...updates } = params;
	devDebug('engineCall', {
		engine: toEngineName(TOOL_NAME_UPDATE_PERSPECTIVE),
		perspectiveOid,
		toolCallId: context.toolCallId,
	});

	const httpClient = context.httpClient ?? getHttpClient();

	await httpClient.patch(`${PERSPECTIVES_URL}/${perspectiveOid}`, updates);

	return { perspectiveOid, updated: true };
}

export async function getPerspectiveEngine(
	params: z.infer<typeof getPerspectiveParamsSchema>,
	context: PerspectiveEngineContext,
): Promise<GetPerspectiveResult> {
	const { perspectiveOid } = params;
	devDebug('engineCall', {
		engine: toEngineName(TOOL_NAME_GET_PERSPECTIVE),
		perspectiveOid,
		toolCallId: context.toolCallId,
	});

	const httpClient = context.httpClient ?? getHttpClient();

	const raw = await httpClient.get<{
		oid: string;
		name?: string;
		description?: string;
		datamodelOid: string;
		tables?: Array<{
			oid: string;
			diffType: 'include' | 'exclude';
			columnsDiff?: Array<{ oid: string; enabled: boolean }>;
		}>;
		relations?: Array<{ oid: string }>;
		isDefault?: boolean;
	}>(`${PERSPECTIVES_URL}/${perspectiveOid}`);

	if (!raw || !raw.oid) {
		throw new Error(`Perspective not found for OID: ${perspectiveOid}`);
	}

	return {
		perspectiveOid: raw.oid,
		name: raw.name ?? '',
		description: raw.description ?? '',
		datamodelOid: raw.datamodelOid,
		tables: (raw.tables ?? []).map((t) => ({
			oid: t.oid,
			diffType: t.diffType,
			columnsDiff: t.columnsDiff ?? [],
		})),
		relations: raw.relations ?? [],
		isDefault: raw.isDefault,
	};
}

export async function listPerspectivesEngine(
	params: z.infer<typeof listPerspectivesParamsSchema>,
	context: PerspectiveEngineContext,
): Promise<ListPerspectivesResult> {
	const { datamodelOid, includeDefault = true } = params;
	devDebug('engineCall', {
		engine: toEngineName(TOOL_NAME_LIST_PERSPECTIVES),
		datamodelOid: datamodelOid ?? null,
		includeDefault,
		toolCallId: context.toolCallId,
	});

	const httpClient = context.httpClient ?? getHttpClient();

	const qs: string[] = [];
	if (datamodelOid)
		qs.push(`datamodelOid=${encodeURIComponent(datamodelOid)}`);
	if (!includeDefault) qs.push('isDefault=false');
	const url = qs.length
		? `${PERSPECTIVES_URL}?${qs.join('&')}`
		: PERSPECTIVES_URL;

	const raw = await httpClient.get<
		Array<{
			oid: string;
			name?: string;
			description?: string;
			datamodelOid: string;
			tables?: unknown[];
			isDefault?: boolean;
			lastUpdated?: string;
		}>
	>(url);

	const perspectives = (raw ?? []).map((p) => ({
		perspectiveOid: p.oid,
		name: p.name ?? '',
		description: p.description ?? '',
		datamodelOid: p.datamodelOid,
		tableCount: Array.isArray(p.tables) ? p.tables.length : 0,
		isDefault: !!p.isDefault,
		lastUpdated: p.lastUpdated,
	}));

	return { perspectives };
}

export async function triggerBuildEngine(
	params: z.infer<typeof triggerBuildParamsSchema>,
	context: PerspectiveEngineContext,
): Promise<TriggerBuildResult> {
	const { datamodelOid, wait = true } = params;
	devDebug('engineCall', {
		engine: toEngineName(TOOL_NAME_TRIGGER_BUILD),
		datamodelOid,
		toolCallId: context.toolCallId,
	});

	const httpClient = context.httpClient ?? getHttpClient();

	const { buildOid } = await triggerFullBuild(datamodelOid, httpClient);

	if (!wait) {
		return { datamodelOid, buildOid, status: 'queued' };
	}

	const polled = await pollBuild(buildOid, { httpClient });
	return {
		datamodelOid,
		buildOid,
		status: polled.status,
		durationMs: polled.durationMs,
	};
}
